import {
  InviteSource,
  ArtWorkPayload,
  ArtistDetailType,
  UserHomePageDetail,
  ApplyArtistAuthInfo,
  UpdateArtistAuthInfo,
  ArtistQualificationResult,
  RealNameQualificationResult,
  ArtistRequirementTypeOption,
} from 'common/types';
import { message } from '@fuxi/eevee-ui';
import { FastApiAxiosInstance } from 'common/service/axiosInstances';
import { FastApiStatusCode } from './constants/fastApi';

const artist = {
  //无限加载分页获取创作者列表
  getArtistList: async (params: {
    current: number;
    pageSize: number;
    nextId?: number;
    query?: string;
    requirementType?: string;
    styles?: string[];
  }) => {
    const data = {
      _page: params.current,
      _num: params.pageSize,
      nextId: params.nextId,
      query: params.query,
      style: !params.styles || params.styles?.includes('不限') ? [] : params.styles,
      requirementType: params.requirementType === '不限' ? '' : params.requirementType,
    };
    const { data: resultData } = await FastApiAxiosInstance.post<{
      total: number;
      nextId: number;
      items: ArtistDetailType[];
    }>('/api/v1/artist/artistList', {
      ...data,
    });

    return {
      ...resultData,
      list: resultData.items,
    };
  },
  //普通分页获取创作者列表
  getArtistListByPage: async (params: {
    current: number;
    pageSize: number;
    query?: string;
    requirementType?: string;
    styles?: string[];
  }) => {
    const data = {
      _page: params.current,
      _num: params.pageSize,
      query: params.query,
      style: !params.styles || params.styles?.includes('不限') ? [] : params.styles,
      requirementType: params.requirementType === '不限' ? '' : params.requirementType,
    };
    const { data: resultData } = await FastApiAxiosInstance.post<{
      total: number;
      items: ArtistDetailType[];
    }>('/api/v1/artist/artistListByPage', {
      ...data,
    });

    return {
      ...resultData,
      list: resultData.items,
    };
  },
  //获取创作者类型，也是任务类型
  getRequirementType: () => {
    return FastApiAxiosInstance.get<string[]>('/api/v1/artist/getRequirementType');
  },
  //运营后台获取创作者详情
  getArtistQualification: async (userId: number) => {
    const { data } = await FastApiAxiosInstance.get('/api/v1/backend/artist/getUserArtistQualificationInfo', {
      params: { userId },
    });
    return data;
  },

  //获取个人主页信息
  getHomePageInfo: (userId: number) => {
    return FastApiAxiosInstance.get<UserHomePageDetail>('/api/v1/user/getUserHomePage', {
      params: { userId },
    });
  },
  // 获取作品详情
  getArtWorkDetail: async (artWorkId: number) => {
    const { data } = await FastApiAxiosInstance.get('/api/v1/artist/artistArtworkDetail', {
      params: { artWorkId },
    });
    return data;
  },
  // 获取创作者作品列表
  getArtistArtworkList: async (params?: { userId?: number }) => {
    const { data } = await FastApiAxiosInstance.get('/api/v2/artist/getUserArtworkList', { params });
    return data;
  },
  // 获取游戏列表
  getGameList: async (params?: { name?: string }) => {
    const { data } = await FastApiAxiosInstance.get('/api/v2/work/style/game/list', { params });
    return data;
  },

  // 批量提交作品
  uploadArtWorks: async (artworks: ArtWorkPayload[]): Promise<ExtendAxioResponse<any>> => {
    // 格式化数据
    const formattedArtworks = artworks.map((item) => {
      const styleIds = item?.style?.map((style) => style?.id);
      return {
        ...item,
        artworkName: item.artworkName,
        artworkUrl: item.artworkUrl,
        previewUrl: item.previewUrl,
        requirementTypeId: item.requirementType,
        style: styleIds,
      };
    });
    const data = await FastApiAxiosInstance.post('/api/v1/artist/uploadArtistArtwork', formattedArtworks);
    return data;
  },
  //获取认证信息
  getArtistQualificationInfo: async (qualificationId: number): Promise<ExtendAxioResponse<any>> => {
    const data = await FastApiAxiosInstance.get('/api/v1/backend/artist/getUserArtistQualificationInfo', {
      params: { qualificationId },
    });
    return data;
  },
  //获取认证信息
  getUserAuthInfo: async (certifyId: number): Promise<ExtendAxioResponse<any>> => {
    const data = await FastApiAxiosInstance.get('/api/v1/backend/user/certifyInformation', {
      params: { certifyId },
    });
    return data;
  },
  //创作者资质申请
  applyArtistQualification: async (params: ApplyArtistAuthInfo): Promise<ExtendAxioResponse<any>> => {
    return FastApiAxiosInstance.post('/api/v1/artist/addUserArtistQualification', { ...params });
  },
  // 创作者资质修改
  updateArtistQualification: async (params: UpdateArtistAuthInfo) => {
    return FastApiAxiosInstance.post('/api/v1/artist/updateUserArtistQualification', { ...params });
  },

  //审核资质
  auditArtistQualification: async (params: ArtistQualificationResult): Promise<ExtendAxioResponse<any>> => {
    return FastApiAxiosInstance.post('/api/v1/backend/artist/checkArtistQualification', { ...params });
  },
  //审核海外实名
  auditAbroadAuth: async (params: RealNameQualificationResult): Promise<ExtendAxioResponse<any>> => {
    return FastApiAxiosInstance.post('/api/v1/backend/user/checkCertifyInformation', { ...params });
  },
  // 邀请创作者
  inviteArtist: async (parentTaskId: number, userId: number, inviteSource?: InviteSource, recommendTaskId?: number) => {
    const res = await FastApiAxiosInstance.get<undefined | null>('/api/v1/artist/inviteArtist', {
      params: { parentTaskId, userId, inviteSource, recommendTaskId },
    });

    if (res.code === FastApiStatusCode.UserHasBeenDelete || res.code === FastApiStatusCode.YSExclusiveUsers) {
      message.error('该用户已下架，邀请失败');
      return Promise.reject(res);
    } else if (res.code === FastApiStatusCode.YXBlackUsers) {
      message.error('该用户为营销不合作创作者，邀请失败');
      return Promise.reject(res);
    } else if (res.code === FastApiStatusCode.YSBlackUsers) {
      message.error('该用户为艺设不合作创作者，邀请失败');
      return Promise.reject(res);
    }

    return res;
  },
  // 获取审核不通过原因
  getRejectReason: async (): Promise<ExtendAxioResponse<any>> => {
    return FastApiAxiosInstance.get('/api/v1/backend/artist/artistReviewInformationList');
  },
  deleteArtwork: async (artworkId: number): Promise<ExtendAxioResponse<any>> => {
    return FastApiAxiosInstance.post(`/api/v1/artist/deleteArtistArtwork`, { artworkId });
  },
};
export default artist;
